import React, { FC, useCallback } from 'react';
import { NetworkStatus } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import Button, { BUTTON_TYPES, BUTTON_WIDTHS } from 'buyside/components/presentation/Button/button';
import IndexPageHeader from 'buyside/components/presentation/IndexPageHeader/indexPageHeader';
import IndexPageListArea, {
  BackToAllButton,
  RecommendedOnlyButton
} from 'buyside/components/presentation/IndexPageListArea/indexPageListArea';
import IndexPaginationNav from 'buyside/components/presentation/IndexPaginationNav/indexPaginationNav';
import MembersIndexCard from 'buyside/components/presentation/MembersIndexPage/MembersIndexCard/membersIndexCard';
import MemberIndexCardLoading from 'buyside/components/presentation/MembersIndexPage/MembersIndexCard/membersIndexCardLoading';
import MembersIndexFilters, {
  renderMembersCheckbox
} from './MembersIndexFilters/membersIndexFilters';
import RecommendedUserCard, {
  RecommendationLoading
} from 'buyside/components/presentation/MembersIndexPage/RecommendedUserCard/RecommendedUserCard';

import { validatePageParam } from 'buyside/util/numbers';
import { BREAKPOINTS, useIsMobile } from 'buyside/util/hooks/useIsMobile';
const { indexTypes } = require('buyside/util/constantsFromRails.json');

import {
  useRecommendedUsersToFollowQuery,
  useUserProfileIndexListQuery
} from 'interfaces/graphql-typings';

import styles from './membersIndexPageStyles.scss';

const MembersIndexPage: FC = () => {
  const [searchParams] = useSearchParams();
  const page = validatePageParam(searchParams);
  const showRecommendationsOnly = searchParams.get('recommended') === 'true';

  const isMobile = useIsMobile(BREAKPOINTS.LARGE);

  const { data, loading, error, previousData } = useUserProfileIndexListQuery({
    variables: { searchParams: searchParams.toString(), indexType: indexTypes.user },
    skip: showRecommendationsOnly
  });

  const {
    data: recData,
    loading: recLoading,
    error: recError,
    fetchMore,
    networkStatus
  } = useRecommendedUsersToFollowQuery({
    skip:
      Boolean(
        Array.from(searchParams.keys()).filter(key => !['page', 'recommended'].includes(key)).length
      ) || !(page === 1 || showRecommendationsOnly),
    notifyOnNetworkStatusChange: true
  });

  if (recError) throw recError;
  if (error) throw error;

  const indexListResult = (data || previousData)?.userProfileIndexList;
  const indexListResultCount = (data || previousData)?.userProfileIndexListResultCount || 0;
  const isCapIntroMember = (data || previousData)?.currentUser?.isCapIntroMember;
  const canViewUserDetails = (data || previousData)?.currentUser?.canViewUserDetails;
  const recEdges = recData?.recommendedUsersToFollow?.edges;
  const recPageInfo = recData?.recommendedUsersToFollow?.pageInfo;

  const renderCheckboxes = useCallback(
    () => renderMembersCheckbox(!isCapIntroMember || showRecommendationsOnly),
    [isCapIntroMember, showRecommendationsOnly]
  );
  const renderFilters = useCallback(
    () => <MembersIndexFilters disabled={!canViewUserDetails || showRecommendationsOnly} />,
    [canViewUserDetails, showRecommendationsOnly]
  );

  return (
    <main className={styles.main}>
      <div className={styles.indexPageContainer}>
        <div className={styles.mainIndexPageArea}>
          <IndexPageHeader
            header={showRecommendationsOnly ? 'Recommended Members' : 'Members'}
            renderCheckboxes={renderCheckboxes}
            renderFilters={renderFilters}
            resultCount={
              showRecommendationsOnly
                ? recData?.recommendedUsersToFollowCount || 0
                : indexListResultCount
            }
            showMoreLess={useIsMobile(BREAKPOINTS.LARGER)}
          />
          <div>{showRecommendationsOnly ? <BackToAllButton /> : <RecommendedOnlyButton />}</div>
          {showRecommendationsOnly ? (
            <>
              {networkStatus === NetworkStatus.loading ? (
                <>
                  {[0, 1, 2, 3, 4].map(i => (
                    <RecommendationLoading key={i} />
                  ))}
                </>
              ) : (
                <>
                  {recEdges?.map(edge =>
                    edge?.node ? (
                      <RecommendedUserCard
                        key={edge.node.id}
                        user={edge.node}
                        canViewUserDetails={canViewUserDetails}
                      />
                    ) : null
                  )}
                  {networkStatus === NetworkStatus.fetchMore && (
                    <>
                      {[0, 1].map(i => (
                        <RecommendationLoading key={i} />
                      ))}
                    </>
                  )}
                  {recPageInfo?.hasNextPage && (
                    <Button
                      id="load-more"
                      disabled={networkStatus === NetworkStatus.fetchMore}
                      onClick={(): void => {
                        fetchMore({ variables: { afterCursor: recPageInfo?.endCursor } });
                      }}
                      text="Load More"
                      type={BUTTON_TYPES.PRIMARY}
                      width={BUTTON_WIDTHS.FULL}
                    />
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {recLoading ? (
                <>
                  {[0, 1].map(i => (
                    <RecommendationLoading key={i} />
                  ))}
                </>
              ) : (
                <>
                  {recEdges
                    ?.slice(0, 2)
                    ?.map(edge =>
                      edge?.node ? (
                        <RecommendedUserCard
                          key={edge.node.id}
                          user={edge.node}
                          canViewUserDetails={canViewUserDetails}
                        />
                      ) : null
                    )}
                </>
              )}
              <IndexPageListArea
                data={indexListResult}
                loading={loading}
                currentUser={(data || previousData)?.currentUser}
                card={MembersIndexCard}
                loadingCard={MemberIndexCardLoading}
                isMobile={isMobile}
                showOnlyRecommended={showRecommendationsOnly}
              />
              <IndexPaginationNav
                loading={loading}
                resultCount={indexListResultCount}
                resultLength={indexListResult?.edges?.length || 0}
                resultsPerPage={20}
              />
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default MembersIndexPage;
